import { Focusable, useSpatialNavigation } from "@blacknut/spatialnav-sdk/dist";
import clsx from "clsx";
import React from "react";
import styles from "./styles.module.scss";

import { ReactComponent as UncheckedCircle } from "../../assets/ic_check_circle.svg";
import { ReactComponent as CheckedCircle } from "../../assets/ic_check_circle_checked.svg";
import { ReactComponent as Unchecked } from "../../assets/ic_check_square.svg";
import { ReactComponent as Checked } from "../../assets/ic_check_square_checked.svg";
declare type CheckboxStyle = {
  activeTintColor?: string;
  inactiveTintColor?: string;
};
export interface CheckboxProps {
  onCheckChange: (e: React.UIEvent<HTMLElement>) => void;
  label?: string | (() => string | JSX.Element);
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  radio?: boolean;
  disabled?: boolean;
  labelAlignment?: "start" | "end";
  testID?: string;
  id: string;
  asHTML?: boolean;
  focusable?: boolean;
  checkboxStyle?: CheckboxStyle;
}

declare type ResultProps = CheckboxProps &
  React.InputHTMLAttributes<HTMLInputElement>;

const Content = ({
  id,
  label,
  radio,
  disabled,
  checked,
  onCheckChange,
  children,
  testID,
  asHTML = false,
  checkboxStyle,
}: React.PropsWithChildren<ResultProps>) => {
  const _label =
    typeof label === "string"
      ? label
      : typeof label === "function"
        ? label()
        : undefined;
  return (
    <>
      <div
        className={clsx([
          styles.checkboxContainer,
          checked && styles.checked,
          radio && styles.radio,
        ])}
        style={
          checked && checkboxStyle?.activeTintColor
            ? { color: checkboxStyle?.activeTintColor }
            : !checked && checkboxStyle?.inactiveTintColor
              ? { color: checkboxStyle?.inactiveTintColor }
              : undefined
        }
      >
        <input
          type={radio ? "radio" : "checkbox"}
          id={id}
          onChange={
            onCheckChange as unknown as React.ChangeEventHandler<HTMLInputElement>
          }
          checked={checked}
          disabled={disabled}
          data-testid={testID}
        />
        {checked && !radio && <Checked onClick={onCheckChange} />}
        {!checked && !radio && <Unchecked onClick={onCheckChange} />}
        {checked && radio && <CheckedCircle onClick={onCheckChange} />}
        {!checked && radio && <UncheckedCircle onClick={onCheckChange} />}
      </div>

      {_label && asHTML && typeof _label === "string" && (
        <label
          className={styles.label}
          htmlFor={id}
          dangerouslySetInnerHTML={{ __html: _label }}
        />
      )}
      {_label && !asHTML && typeof _label === "string" && (
        <label className={clsx(styles.label)} htmlFor={id}>
          {_label}
        </label>
      )}

      {_label && typeof _label !== "string" && _label}

      {children}
    </>
  );
};
const Checkbox = (props: React.PropsWithChildren<ResultProps>) => {
  const {
    focusable = true,
    disabled,
    labelAlignment = "end",
    className,
    onCheckChange,
  } = props;
  const { active: spatialNavigationActive } = useSpatialNavigation();
  const _onCheckChange = React.useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      if (disabled && e) {
        e.preventDefault();
        e.stopPropagation();
      } else if (!disabled) {
        onCheckChange(e);
      }
    },
    [disabled, onCheckChange]
  );
  return (
    <>
      {focusable && (
        <Focusable
          className={clsx([
            styles.container,
            spatialNavigationActive && styles.focusable,
            disabled && styles.disabled,
            labelAlignment === "end" && styles.endAligned,
            className,
          ])}
          onPressEnter={_onCheckChange}
        >
          <Content {...props} onCheckChange={_onCheckChange}></Content>
        </Focusable>
      )}
      {!focusable && (
        <div
          className={clsx([
            styles.container,
            disabled && styles.disabled,
            labelAlignment === "end" && styles.endAligned,
            className,
          ])}
        >
          <Content {...props} onCheckChange={_onCheckChange}></Content>{" "}
        </div>
      )}
    </>
  );
};
export default Checkbox;
export { Checkbox };
